import type { DateRange } from "@cubejs-client/core";
import moment from "moment-timezone";
import { err, isErr, ok, type Result } from "../../../util/result";
import {
  normalizeDateFilterValues,
  resolveDateRange,
} from "../vendor/cube/query";
import { UserError } from "../vendor/cube/user-error";

export type DateIssue =
  | "unparsed-date"
  | "invalid-date"
  | "relative-date-needs-one-value";

type DayTime = "T00:00:00.000" | "T23:59:59.999";
type Clock = { timezone: string; now: Date };

const LOCAL_MS = moment.HTML5_FMT.DATETIME_LOCAL_MS;
const LOCAL_MS_TIME = /^\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d\.\d\d\d$/;
const DAY = /^\d\d\d\d-\d\d-\d\d$/;
const MIDNIGHT: DayTime = "T00:00:00.000";

const readByCube = <T>({
  read,
  issue,
}: {
  read: () => T;
  issue: DateIssue;
}): Result<T, DateIssue> => {
  try {
    return ok(read());
  } catch (error) {
    if (error instanceof UserError) {
      return err(issue);
    }
    throw error;
  }
};

const isLocalTime = (time: string) =>
  moment.utc(time, LOCAL_MS, true).isValid();

const validTimes = <Times extends string[]>(
  times: Times,
): Result<Times, DateIssue> =>
  times.every(isLocalTime) ? ok(times) : err("invalid-date");

// BaseFilter.formatFromDate and formatToDate at the millisecond precision Postgres and Snowflake use.
const formatDate = ({
  date,
  timezone,
  dayTime,
}: {
  date: string;
  timezone: string;
  dayTime: DayTime;
}): string => {
  if (LOCAL_MS_TIME.test(date)) {
    return date;
  }
  return DAY.test(date)
    ? `${date}${dayTime}`
    : moment.tz(date, timezone).format(LOCAL_MS);
};

/** The first day whose midnight is at or after a wall-clock time, which is how a DATE column compares to Cube's timestamp. */
export const dayAtOrAfter = (time: string): string => {
  const day = time.slice(0, 10);
  return time.endsWith(MIDNIGHT)
    ? day
    : moment.utc(day).add(1, "day").format("YYYY-MM-DD");
};

export const dayAtOrBefore = (time: string): string => time.slice(0, 10);

export const todayIn = ({ timezone, now }: Clock): string =>
  moment(now).tz(timezone).format("YYYY-MM-DD");

/** A Cube dateRange as the gateway resolves it: wall-clock times in the query's time zone. */
export function dateRangeTimes({
  dateRange,
  timezone,
  now,
}: Clock & { dateRange: DateRange }): Result<[string, string], DateIssue> {
  const times = readByCube({
    read: () => resolveDateRange(dateRange, timezone, now),
    issue: "unparsed-date",
  });
  if (isErr(times)) {
    return times;
  }
  const [from, to] = times.value;
  if (from === undefined || to === undefined) {
    throw new Error("resolveDateRange returns a pair for a string or a pair");
  }
  return validTimes<[string, string]>([from, to]);
}

/** The first and last day of a DATE column a Cube dateRange covers; an inverted range stays inverted and selects nothing. */
export function dateRangeDays(
  args: Clock & { dateRange: DateRange },
): Result<[string, string], DateIssue> {
  const times = dateRangeTimes(args);
  return isErr(times)
    ? times
    : ok([dayAtOrAfter(times.value[0]), dayAtOrBefore(times.value[1])]);
}

/** A date filter's values as Cube's normalizeDateFilterValues leaves them: a relative string resolved, absolute values untouched. */
export const normalizedValues = ({
  operator,
  values,
  timezone,
  now,
}: Clock & { operator: string; values: string[] }): Result<
  string[],
  DateIssue
> =>
  readByCube({
    read: () =>
      normalizeDateFilterValues({ operator, values }, timezone, now).values,
    issue:
      values.length > 1 ? "relative-date-needs-one-value" : "unparsed-date",
  });

export function dateRangeFilterTimes(
  args: Clock & {
    operator: "inDateRange" | "notInDateRange";
    values: string[];
  },
): Result<[string, string], DateIssue> {
  const values = normalizedValues(args);
  if (isErr(values)) {
    return values;
  }
  const [from, to] = values.value;
  if (from === undefined || to === undefined) {
    throw new Error("A range filter reaches BaseFilter with two values");
  }
  const { timezone } = args;
  return validTimes<[string, string]>([
    formatDate({ date: from, timezone, dayTime: MIDNIGHT }),
    formatDate({ date: to, timezone, dayTime: "T23:59:59.999" }),
  ]);
}

export function dateBoundFilterTime(
  args: Clock & {
    operator: "beforeDate" | "beforeOrOnDate" | "afterDate" | "afterOrOnDate";
    values: string[];
  },
): Result<string, DateIssue> {
  const values = normalizedValues(args);
  if (isErr(values)) {
    return values;
  }
  const [value] = values.value;
  if (value === undefined) {
    throw new Error("A date filter reaches BaseFilter with a value");
  }
  const time = formatDate({
    date: value,
    timezone: args.timezone,
    dayTime: MIDNIGHT,
  });
  return isLocalTime(time) ? ok(time) : err("invalid-date");
}
